import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSetupIntentSecretClient } from '../../services/vault/intent';
import { setDefaultPaymentMethodStripeConnectAccount } from '../../services/vault/paymentMethods';
import { getStripeClient, loadStripeClientScript } from './utils';
import { getStripeConnectAccountBankAccounts } from '../../services/vault/bankAccount';
import { FinancialAccountNode } from '../../services/orgs/interfaces';
import { BankAccount, StripeConnectAccount } from './props';
import ChangePayoutIntervalModal from './components/ChangePayoutIntervalModal';
import FullPageLoader from './components/FullPageLoader';
import OrgsService from '../../services/orgs';

import { getStripeConnectAccount } from '../../services/vault/stripeAccount';
import { createCustomer } from '../../services/vault/customer';
import { updateFinancialAccountNode } from '../../services/orgs/financialAcctUpdate';
import { setStripeConnectAccountPayoutFrequency } from '../../services/vault/payout';
import { StripeEnv } from '../../util/enums';
import logger from '../../util/logger';
import useSentry from '../../hooks/useSentry';
import AddButton from './components/AddButton';
import BankAccountCard from './components/BankAccountCard';

export interface BankAccountManagerProps {
  stripeConnectAccountId?: string;
  stripeEnv?: StripeEnv;
  onPayoutIntervalSelection?: (selectedInterval: string) => void;
  setHasBankAccount?: (hasBankAccount: boolean) => void;
  setError: (errorMessage: string) => void;
}

type FormState = 'waiting' | 'success' | 'error';

export default function BankAccountManagerInsights({
  stripeConnectAccountId,
  stripeEnv = StripeEnv.RAISE,
  onPayoutIntervalSelection,
  setHasBankAccount,
}: BankAccountManagerProps): JSX.Element {
  useSentry();
  const [isStripeLoaded, setIsStripeLoaded] = useState(false);
  const [stripeClient, setStripeClient] = useState<StripeClientInterface>();
  const [submitState, setSubmitState] = useState<FormState>();
  const [bankAccountList, setBankAccountList] = useState<BankAccount[]>();
  const [stripeConnectAccount, setStripeConnectAccount] =
    useState<StripeConnectAccount>();
  const [collectBankAccountModalIsOpen, setCollectBankAccountModalIsOpen] =
    useState(false);

  const [isSetFreqModalOpen, setIsSetFreqModalOpen] = useState(false);
  const [showFreqModalHeader, setShowFreqModalHeader] = useState(false);
  const [financialAccountNode, setFinancialAccountNode] =
    useState<FinancialAccountNode>();
  const financialAccountNodeFetched = useRef(false);
  const [customerId, setCustomerId] = useState<string>();

  useEffect(() => {
    logger('useEffect fetchFinancialAccount');
    if (financialAccountNodeFetched.current) {
      return;
    }
    async function fetchFinancialAccountData(): Promise<void> {
      if (stripeConnectAccountId === undefined) {
        return;
      }
      financialAccountNodeFetched.current = true;
      const financialAccount = await OrgsService.financialAcctId(
        stripeConnectAccountId,
      );
      if (financialAccount === undefined) {
        throw new Error(
          `Could not find Financial Account Node  for Stripe Connect Account Id ${stripeConnectAccountId}`,
        );
      }
      if (
        financialAccount.finAcctId === undefined ||
        financialAccount.finAcctId === null
      ) {
        throw new Error(
          `Financial Account Node for Stripe Connect Account Id ${stripeConnectAccountId} is missing finAcctId`,
        );
      }
      if (financialAccount.orgId === undefined) {
        throw new Error(
          `Financial Account Node for Stripe Connect Account Id ${stripeConnectAccountId} is missing orgId`,
        );
      }
      setFinancialAccountNode(financialAccount);
      logger('financialAccount', false, financialAccount);

      if (financialAccount.customerId) {
        logger(`existing customer ${financialAccount.customerId}`);
        setCustomerId(financialAccount.customerId);
      } else {
        logger('customerId is UNDEFINED');
        const customerIdResponse = await createCustomer({
          payableOrgName: stripeConnectAccountId,
          stripeEnv,
        });
        logger('customerId', false, customerIdResponse);
        setCustomerId(customerIdResponse);
        await updateFinancialAccountNode({
          finAcctId: financialAccount.finAcctId,
          orgId: financialAccount.orgId,
          acctId: stripeConnectAccountId,
          customerId: customerIdResponse,
        });
      }
    }
    fetchFinancialAccountData();
  }, [stripeConnectAccountId, stripeEnv]);

  const toggleSetFreqModal = (showHeader: boolean): void => {
    setShowFreqModalHeader(showHeader);
    setIsSetFreqModalOpen(!isSetFreqModalOpen);
  };

  const fetchBankAccountList = useCallback(async (): Promise<void> => {
    if (stripeConnectAccountId === undefined) {
      throw new Error('stripeConnectAccountId is not set');
    }
    const response = await getStripeConnectAccountBankAccounts(
      stripeConnectAccountId,
      stripeEnv,
    );
    setBankAccountList(
      response.filter((b: BankAccount) => b.default_for_currency),
    );
    if (setHasBankAccount !== undefined) {
      setHasBankAccount(response && response.length > 0);
    }
  }, [stripeConnectAccountId, stripeEnv, setHasBankAccount]);

  const fetchStripeConnectAccount = useCallback(
    async (accountId: string): Promise<void> => {
      const response = await getStripeConnectAccount(accountId, stripeEnv);
      if (response !== null) {
        setStripeConnectAccount(response);
      }
    },
    [stripeEnv],
  );
  useEffect(() => {
    (async (): Promise<void> => {
      if (!window.Stripe) {
        await loadStripeClientScript(setStripeClient, stripeEnv);
        setIsStripeLoaded(true);
      } else {
        setStripeClient(getStripeClient(stripeEnv));
        setIsStripeLoaded(true);
      }
    })();
  }, [stripeEnv]);

  useEffect(() => {
    if (stripeConnectAccountId === undefined) {
      return;
    }
    fetchStripeConnectAccount(stripeConnectAccountId);
    fetchBankAccountList();
  }, [fetchBankAccountList, fetchStripeConnectAccount, stripeConnectAccountId]);

  const confirmBankAccountSetup = async (
    clientSecret: string,
  ): Promise<void> => {
    setSubmitState('waiting');
    const { setupIntent, error } = await (
      stripeClient as StripeClientInterface
    ).confirmUsBankAccountSetup(clientSecret);
    logger('confirmUsBankAccountSetup setup intent', false, setupIntent);
    if (error) {
      setSubmitState('error');
      return;
    } else if (customerId !== undefined) {
      if (
        setupIntent.status === 'succeeded' &&
        stripeConnectAccountId !== undefined
      ) {
        logger(`setupIntent status ${setupIntent.status}`);
        logger(`customerId ${customerId}`);
        logger(
          `financialAccountNode?.customerId ${financialAccountNode?.customerId}`,
        );
        logger('setupIntent payment_method', false, setupIntent.payment_method);
        await setDefaultPaymentMethodStripeConnectAccount({
          stripeConnectAccountId,
          paymentMethodId: setupIntent.payment_method,
          customerId,
          stripeEnv,
          orgId: financialAccountNode?.orgId,
        });
        await updateFinancialAccountNode({
          finAcctId: financialAccountNode?.finAcctId as string,
          orgId: financialAccountNode?.orgId as string,
          acctId: stripeConnectAccountId,
          paymentMethodId: setupIntent.payment_method,
        });
        if (bankAccountList?.length === 0) {
          toggleSetFreqModal(true);
        }

        setSubmitState('success');
        await fetchBankAccountList();
      } else {
        // Unable to confirm the SetupIntent, re-fetch the SetupIntent for details
        return;
      }
    }
  };

  const collectBankAccountDetails = async (): Promise<void> => {
    if (customerId == undefined) {
      throw new Error('customerId is not set');
    }
    if (stripeConnectAccountId === undefined) {
      throw new Error('stripeConnectAccountId is not set');
    }
    setCollectBankAccountModalIsOpen(true);
    const clientSecret = await getSetupIntentSecretClient({
      customerId,
      stripeEnv,
    });
    setCollectBankAccountModalIsOpen(false);
    if (clientSecret && stripeClient) {
      const { setupIntent, error } =
        await stripeClient.collectBankAccountForSetup({
          clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: {
                name: stripeConnectAccountId,
              },
            },
          },

          expand: ['payment_method'],
        });
      if (error) {
        setSubmitState('error');
      }
      if (setupIntent.status === 'requires_confirmation') {
        confirmBankAccountSetup(clientSecret);
      } else {
        return;
      }
    }
  };

  const onUpdatePayoutFrequency = async (
    payoutIntervalSelected: string,
  ): Promise<void> => {
    if (payoutIntervalSelected === undefined) {
      throw new Error('Payout Interval Not Selected');
    }
    if (stripeConnectAccountId === undefined) {
      throw new Error('stripeConnectAccountId is not set');
    }
    toggleSetFreqModal(false);
    setSubmitState('waiting');
    await setStripeConnectAccountPayoutFrequency(
      stripeConnectAccountId,
      payoutIntervalSelected,
      stripeEnv,
      financialAccountNode?.orgId,
    );
    await fetchStripeConnectAccount(stripeConnectAccountId);
    await fetchBankAccountList();
    if (onPayoutIntervalSelection !== undefined) {
      onPayoutIntervalSelection(payoutIntervalSelected);
    }
    setSubmitState('success');
  };
  return (
    <div>
      <ChangePayoutIntervalModal
        isOpen={isSetFreqModalOpen}
        showHeader={showFreqModalHeader}
        stripeEnv={stripeEnv}
        onUpdate={onUpdatePayoutFrequency}
        onCancel={(): void => toggleSetFreqModal(false)}
        toggle={(): void => toggleSetFreqModal(false)}
        defaultInterval={
          stripeConnectAccount?.settings.payouts.schedule.interval
        }
      />
      {bankAccountList === undefined ? (
        <FullPageLoader />
      ) : (
        <React.Fragment>
          <div className='max-w-2xl mx-auto p-4'>
            <div className='m-2'>
              {submitState === 'waiting' ? <FullPageLoader /> : null}
            </div>

            {bankAccountList.length === 0 ? (
              <div className='mt-4 flex justify-start xs:justify-start md:justify-start gap-x-2'>
                <AddButton
                  onClick={collectBankAccountDetails}
                  disabled={
                    !isStripeLoaded ||
                    !customerId ||
                    customerId.length === 0 ||
                    collectBankAccountModalIsOpen
                  }
                />
              </div>
            ) : (
              <div className='flex flex-col'>
                <BankAccountCard
                  bankAccount={bankAccountList[0]}
                  payoutInterval={
                    stripeConnectAccount?.settings.payouts.schedule.interval
                  }
                  onUpdatePayoutClick={(): void => {
                    setIsSetFreqModalOpen(true);
                  }}
                  disabledReplaceAccount={
                    !isStripeLoaded ||
                    !customerId ||
                    customerId.length === 0 ||
                    collectBankAccountModalIsOpen
                  }
                  onReplaceAccountClick={collectBankAccountDetails}
                />
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
}
